import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import queryClient from 'api/queryClient'

export const STRIPE_AI_BILLING_STATUS_QUERY_KEY = ['stripeAiBillingStatus']
export const STRIPE_AI_BILLING_STATUS_STALE_MS = 30 * 1000

export type AiCreditEntry = {
    id: string
    kind: 'topup' | 'hold' | 'charge' | 'release' | 'refund' | 'adjustment'
    status: string
    amount_cents: number
    balance_after_cents: number
    created_at: string
}

export type AiCreditsStatus = {
    balance_cents: number
    auto_reload: boolean
    reload_cents: number
    threshold_cents: number
    card?: string | null
    card_saved: boolean
    reload_failed_at?: string | null
    reload_failure?: string | null
    topup_options_cents: number[]
    entries: AiCreditEntry[]
}

export type StripeAiBillingStatus = {
    // credits = prepaid balance with auto-reload; settlement = pay-as-you-go card billing.
    usd?: {
        mode: 'credits' | 'settlement'
        payable: boolean
        credits?: AiCreditsStatus
    }
    stripe?: {
        customer_id?: string | null
        subscription_id?: string | null
        subscription_status?: string | null
        ai_payment_mode?: string | null
        metered_locked_at?: string | null
        metered_lock_reason?: string | null
        refreshed_at?: string | null
    }
    usage?: unknown
}

export const getCachedStripeAiBillingStatus = (): StripeAiBillingStatus | undefined =>
    queryClient.getQueryData(STRIPE_AI_BILLING_STATUS_QUERY_KEY) as
        | StripeAiBillingStatus
        | undefined

export const setCachedStripeAiBillingPaymentMode = (mode: 'bsv' | 'stripe_metered') => {
    queryClient.setQueryData(
        STRIPE_AI_BILLING_STATUS_QUERY_KEY,
        (current: StripeAiBillingStatus | undefined) => ({
            ...(current || {}),
            stripe: {
                ...(current?.stripe || {}),
                ai_payment_mode: mode,
            },
        }),
    )

    const currentUser = window?.gon?.currentUser as unknown as
        | { ai_payment_mode?: 'bsv' | 'stripe_metered' }
        | undefined
    if (currentUser) {
        currentUser.ai_payment_mode = mode
    }
}

export const getStripeAiBillingStatusCacheAgeMs = (): number | null => {
    const updatedAt =
        queryClient.getQueryState(STRIPE_AI_BILLING_STATUS_QUERY_KEY)?.dataUpdatedAt || 0
    return updatedAt > 0 ? Date.now() - updatedAt : null
}

export const isStripeAiBillingStatusCacheFresh = (): boolean => {
    const ageMs = getStripeAiBillingStatusCacheAgeMs()
    return ageMs != null && ageMs < STRIPE_AI_BILLING_STATUS_STALE_MS
}

export const refreshStripeAiBillingStatus = (refresh: boolean = false) =>
    queryClient.fetchQuery({
        queryKey: STRIPE_AI_BILLING_STATUS_QUERY_KEY,
        queryFn: () => api.getStripeAiBillingStatus(refresh),
        staleTime: 0,
    })

export default function useStripeAiBillingStatus(refresh: boolean = false) {
    return useQuery({
        queryKey: STRIPE_AI_BILLING_STATUS_QUERY_KEY,
        queryFn: () => api.getStripeAiBillingStatus(refresh),
        staleTime: STRIPE_AI_BILLING_STATUS_STALE_MS,
    })
}
